import { Link } from 'react-router-dom';
import { ChevronDown, Calendar, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Countdown } from './Countdown';
import { useLanguage } from '@/context/LanguageContext';

export const Hero = () => {
    const { t } = useLanguage();
    
    const scrollToContent = () => {
        window.scrollTo({
            top: window.innerHeight - 80,
            behavior: 'smooth'
        });
    };

    return (
        <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
            {/* Background Image */}
            <div 
                className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                style={{
                    backgroundImage: `url('https://images.unsplash.com/photo-1492538368677-f6e0afe31dcc?auto=format&fit=crop&w=2000&q=80')`
                }}
            />
            
            {/* Overlay */}
            <div className="hero-overlay absolute inset-0" />
            
            {/* Subtle Pattern Overlay */}
            <div 
                className="absolute inset-0 opacity-5"
                style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
                }}
            />

            {/* Content */}
            <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                {/* Main Title */}
                <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold text-primary-foreground mb-4 fade-in-up">
                    {t('home.heroTitle1')}
                    <span className="block text-accent mt-2">{t('home.heroTitle2')}</span>
                </h1>

                {/* Year Badge */}
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/20 border border-accent/30 text-accent font-semibold mb-8 fade-in-up fade-in-delay-1">
                    <span className="text-lg">2026</span>
                </div>

                {/* Tagline */}
                <p className="text-xl sm:text-2xl text-primary-foreground/80 font-light max-w-2xl mx-auto mb-8 fade-in-up fade-in-delay-1">
                    {t('home.tagline')}
                </p>

                {/* Date and Location */}
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 mb-10 fade-in-up fade-in-delay-2">
                    <div className="flex items-center gap-2 text-primary-foreground/90">
                        <Calendar className="w-5 h-5 text-accent" />
                        <span className="font-medium">27 - 28 - 29 March 2026</span>
                    </div>
                    <div className="hidden sm:block w-1.5 h-1.5 rounded-full bg-accent" />
                    <div className="flex items-center gap-2 text-primary-foreground/90">
                        <MapPin className="w-5 h-5 text-accent" />
                        <span className="font-medium">Adana – Girne College Campus</span>
                    </div>
                </div>

                {/* Countdown */}
                <div className="mb-12 fade-in-up fade-in-delay-2">
                    <Countdown targetDate="2026-03-27T09:00:00" />
                </div>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4 fade-in-up fade-in-delay-3">
                    <Link to="/apply">
                        <Button 
                            size="lg" 
                            className="btn-premium px-8 py-6 text-lg font-semibold rounded-lg text-primary hover:text-primary min-w-[200px]"
                        >
                            <span>{t('nav.applyNow')}</span>
                        </Button>
                    </Link>
                    <Link to="/about">
                        <Button 
                            variant="outline" 
                            size="lg"
                            className="px-8 py-6 text-lg font-semibold rounded-lg border-2 border-primary-foreground/30 text-primary-foreground bg-transparent hover:bg-primary-foreground/10 hover:text-primary-foreground min-w-[200px]"
                        >
                            {t('home.learnMore')}
                        </Button>
                    </Link>
                </div>
            </div>

            {/* Scroll Indicator */}
            <button
                onClick={scrollToContent}
                className="absolute bottom-8 left-1/2 -translate-x-1/2 text-primary-foreground/60 hover:text-primary-foreground transition-colors animate-bounce"
                aria-label="Scroll down"
            >
                <ChevronDown className="w-8 h-8" />
            </button>

            {/* Decorative Elements */}
            <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent" />
        </section>
    );
};

export default Hero;
